import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import colors from 'styles/colors';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import truncateText from 'utils/truncateText';
import {
  JOB_FAILED_VALIDATION_STATUS,
  JOB_LIMIT_EXCEEDED_STATUS,
  JOB_TERMINATED_STATUS,
  jobStatusIcons,
  jobStatusLabels,
} from 'constants/jobs';
import Bugsnag from '@bugsnag/js';
import Box from '@mui/material/Box';
import useJobCancel from 'hooks/useJobCancel';
import { isJobInProgress } from 'utils/helpers';
import useUserStore from 'store/UserStore';
import React, { useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import JobMetadataLoader from './ContentLoader/JobMetadataLoader';

const JobMetadata = ({ isLoading, metadataStructure, metadata }) => {
  const user = useUserStore((state) => state.user);
  const { cancelJob } = useJobCancel(metadata.jobId);
  const isInProgress = isJobInProgress(metadata.runStatus);
  const canCancel = isInProgress && metadata.userId === user.uid;
  const [isCancelInProgress, setIsCancelInProgress] = useState(false);

  if (isLoading) {
    return <JobMetadataLoader />;
  }

  const handleCancel = async () => {
    const isCanceling = await cancelJob();
    setIsCancelInProgress(isCanceling);
  };

  const renderName = (name) => {
    const { text, tooltipText } = truncateText(name, 35);
    return tooltipText ? (
      <Tooltip title={tooltipText} arrow><span>{text}</span></Tooltip>) : text;
  };

  const getDisplayValue = (key) => {
    if (key === 'jobName') return renderName(metadata[key]);
    if (key === 'runStatus') {
      const Icon = jobStatusIcons[metadata[key]];
      const errorStatuses = [JOB_FAILED_VALIDATION_STATUS, JOB_LIMIT_EXCEEDED_STATUS, JOB_TERMINATED_STATUS];
      const tooltip = errorStatuses.includes(metadata[key]) ? metadata.errorMessage : null;
      if (!Icon) {
        Bugsnag.notify(new Error(`Unknown runStatus: ${metadata[key]}`));
        return null;
      }
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '8px' }}>
            {jobStatusLabels[metadata[key]]}
          </span>
          <Tooltip title={tooltip} arrow>
            <span style={{ display: 'flex' }}><Icon /></span>
          </Tooltip>
          {canCancel ? (
            <LoadingButton
              type="primary"
              variant="contained"
              onClick={handleCancel}
              disabled={isCancelInProgress}
              sx={{
                fontSize: '13px',
                padding: isCancelInProgress ? '6px 30px' : '6px 16px',
              }}
              loading={isCancelInProgress}
              loadingPosition="end"
              endIcon={<div />}
            >
              Cancel
            </LoadingButton>
          ) : null}
        </Box>
      );
    }
    if (key === 'shots' && (metadata[key] === null || metadata[key] === undefined)) {
      return '-';
    }
    return metadata[key];
  };

  const getTooltipTitle = (tooltip, key) => {
    if (!tooltip) return null;
    return typeof tooltip === 'string' ? tooltip : tooltip[metadata[key]?.toLowerCase()];
  };

  return (
    <Grid
      container
      direction="row"
      sx={{ paddingLeft: '30px', paddingRight: '30px' }}
    >
      {
        metadataStructure.map(({ label, key, tooltip }) => {
          // Skip bondDimension if it is null
          if (key === 'mpsBondDimension' && (metadata[key] === null || metadata[key] === undefined)) {
            return null;
          }

          // Skip truncationThreshold if it is null or device is not PAULI-PATH
          if (key === 'mpsTruncationThreshold'
            && (metadata[key] === null || metadata[key] === undefined || metadata.device !== 'PAULI-PATH')) {
            return null;
          }

          return (
            <Grid
              key={key}
              md={6}
              xs={12}
              item
              style={{
                width: '110px',
                paddingRight: '15px',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  width: '27%',
                  paddingRight: '10px',
                  display: 'inline-block',
                }}
              >
                {label}
                {label ? ':' : ''}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.primary, display: 'flex',
                }}
              >
                {getDisplayValue(key)}
                {tooltip && (
                  <Tooltip
                    arrow
                    sx={{ backgroundColor: colors.background.primary }}
                    title={getTooltipTitle(tooltip, key)}
                    placement="right"
                  >
                    <InfoOutlinedIcon
                      sx={{
                        color: colors.text.secondary,
                        fontSize: '16px',
                        paddingLeft: '5px',
                      }}
                    />
                  </Tooltip>
                )}
              </Typography>
            </Grid>
          );
        })
      }
    </Grid>
  );
};

export default JobMetadata;
